import {
  computed,
  inject,
  onScopeDispose,
  provide,
  reactive,
  ref,
  shallowReactive,
  watch,
  type ComputedRef,
  type InjectionKey,
  type Ref,
} from "vue"
import { useDisplay } from "vuetify"
import { useAuthStore } from "@/store/authentication"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"

/**
 * The size of a report panel (the Bautagesbericht and Stundenlohnbericht drawers on the project
 * page): its width, dragged at the left edge; full width; and minimised to a strip at the right edge.
 *
 * A report is written against the project behind it: who was on site, which machine, what the
 * Leistungsverzeichnis says. At a fixed 850px the panel covered exactly the part of the page that
 * answers those questions. Now it can be made narrower to keep the page in view, wider to hold a
 * long shift list, or folded away to a strip while the page is looked up, with the half-written
 * report kept as it was. See docs/report-panels.md.
 */

/** The width a panel opens at until someone drags it. The panel's width before it could be resized. */
const DEFAULT_WIDTH = 850
/**
 * The narrowest a panel settles at. The report forms are laid out for a 360px phone, so 400 still
 * holds them; below that, letting go minimises instead.
 */
const MIN_WIDTH = 400
/** The minimised panel. Wide enough for an icon button, the report's badge and its name on end. */
const STRIP_WIDTH = 52
/** How far a drag may pull the panel in, to say "minimise" before it is let go. */
const DRAG_FLOOR = 160
/** Let go this close to full width and the panel takes all of it, and keeps doing so. */
const FULL_SNAP = 48
/** Below this viewport width the panel is the whole screen, and there is nothing to resize. */
const RESIZE_FROM = 600
/** One arrow key press on the handle. Shift moves four times as far. */
const KEY_STEP = 32

/** `cr` is the Bautagesbericht's panel, `hwr` the Stundenlohnbericht's (the keys `terminology.js` uses). */
export type ReportPanelKey = "cr" | "hwr"

/* -------------------------------------------------------------------------------------------------
 * The dock. Module scope because the two panels on a page share the right edge: a minimised panel
 * docks there as a strip, and the other panel, the app bar and the page's content column all move
 * over for it. `LewBaseLayout` reads `useReportPanelDock` to keep its scroll column clear of the
 * strips, which is the same reach across the tree as `useImmersiveMode`.
 * -----------------------------------------------------------------------------------------------*/

/** The panels showing as a strip right now. */
const docked = reactive(new Set<ReportPanelKey>())
interface Front {
  isFront: () => boolean
  minimize: () => void
}
/** Each mounted panel's "step aside", for when another panel comes to the front. */
const fronts = new Map<ReportPanelKey, Front>()

/** The width the docked strips take off the right edge of the page, for the layout. */
export function useReportPanelDock(): ComputedRef<number> {
  return computed(() => docked.size * STRIP_WIDTH)
}

/* -------------------------------------------------------------------------------------------------
 * What the panels hold, for a bug report: `plugins/sentryFeedbackContext.js` says which report was
 * open, on which tab, and whether it held changes. The URL names only the project page.
 * -----------------------------------------------------------------------------------------------*/

/** The report in a panel, as a bug report may name it: ids, its number and date, our own tab keys. */
export interface ReportPanelReport {
  /** "" while the report is not saved yet. */
  id: string
  /** Its number on the project with the letter, `12b`. */
  number: string
  date: string
  /** The value of the tab on screen (`tabServices`). */
  tab: string
  unsaved: boolean
  /** Its save is on the way. */
  saving: boolean
  /** A printed revision, not the report itself. */
  revision: boolean
}

export interface ReportPanelState extends ReportPanelReport {
  panel: ReportPanelKey
  minimized: boolean
}

type DescribePanel = () => ReportPanelState | null
/** Each mounted panel's report while it is open. Reactive, so a watcher sees one open, change or close. */
const described = shallowReactive(new Map<ReportPanelKey, DescribePanel>())

/** The open panels and their reports. */
export function openReportPanels(): ReportPanelState[] {
  return [...described.values()]
    .map(describe => describe())
    .filter((state): state is ReportPanelState => state !== null)
}

/* -------------------------------------------------------------------------------------------------
 * Remembered width. Per person and workspace, like `useRememberedRegions`: a shared workstation on
 * a building site is normal, and the next person to sign in must not inherit the last one's panel.
 * Every access guarded, reads included, for the reason spelled out there.
 * -----------------------------------------------------------------------------------------------*/

const STORAGE_PREFIX = "reportPanel"

interface StoredSize {
  width?: number
  full?: boolean
}

function storageKey(panel: ReportPanelKey): string | null {
  const authStore = useAuthStore()
  const tenant = authStore.getTenant()
  const userId = authStore.getId
  return tenant && userId ? `${STORAGE_PREFIX}.${tenant}.${userId}.${panel}` : null
}

function readSize(panel: ReportPanelKey): StoredSize {
  const key = storageKey(panel)
  if (!key || IS_VIEW_HELP_EMBED) return {}
  try {
    const stored = JSON.parse(localStorage.getItem(key) ?? "{}")
    return {
      width: Number.isFinite(stored?.width) ? stored.width : undefined,
      full: typeof stored?.full === "boolean" ? stored.full : undefined,
    }
  } catch {
    return {}
  }
}

/** Never in the Erklärung's frame, which shares an origin with the reader's own page. */
function writeSize(panel: ReportPanelKey, size: Required<StoredSize>) {
  const key = storageKey(panel)
  if (!key || IS_VIEW_HELP_EMBED) return
  try {
    localStorage.setItem(key, JSON.stringify(size))
  } catch {
    // Quota or a blocked store: the panel keeps working, it just opens at the default next time.
  }
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

/* -------------------------------------------------------------------------------------------------
 * The panel.
 * -----------------------------------------------------------------------------------------------*/

export interface ReportPanel {
  /** The width the drawer is drawn at right now, the strip included. */
  width: ComputedRef<number>
  minimized: Ref<boolean>
  /** Drawn at all the width there is. */
  isFull: ComputedRef<boolean>
  /** Narrow enough for the phone layout of the header, whatever the viewport. */
  isNarrow: ComputedRef<boolean>
  /** The viewport leaves a width to choose. Below {@link RESIZE_FROM} the panel is the screen. */
  resizable: ComputedRef<boolean>
  /** Full width is a different width from the panel's own, so the toggle does something. */
  canToggleFull: ComputedRef<boolean>
  /** While dragging: what letting go now would do, when it is more than setting the width. */
  dragIntent: ComputedRef<"minimize" | "full" | null>
  dragging: ComputedRef<boolean>
  /** The handle's value range, for `aria-valuemin` / `aria-valuemax`. */
  minWidth: number
  maxWidth: ComputedRef<number>
  minimize: () => void
  restore: () => void
  toggleFull: () => void
  resetWidth: () => void
  /** Closes it: ✕, Esc, a double click on the scrim. Asks `beforeClose` first. */
  close: () => Promise<void>
  /**
   * Whether the panel may be pointed at another report now. Only a minimised panel can be asked:
   * open, it covers the page, so nothing behind it can open a report. Minimised, it holds a report
   * someone left for later, so `confirm` decides before it is thrown away (the drawer asks only when
   * it holds unsaved changes). Agreeing brings the panel back.
   */
  mayReplace: (_confirm: () => Promise<boolean>) => Promise<boolean>
  handle: {
    onPointerdown: (_event: PointerEvent) => void
    onPointermove: (_event: PointerEvent) => void
    onPointerup: (_event: PointerEvent) => void
    onPointercancel: (_event: PointerEvent) => void
    onKeydown: (_event: KeyboardEvent) => void
    onDblclick: () => void
  }
}

const REPORT_PANEL: InjectionKey<ReportPanel> = Symbol("lew:report-panel")

/** The panel around a component, or null outside one. */
export const useReportPanelContext = () => inject(REPORT_PANEL, null)

/**
 * The size state of one report panel, provided to everything inside it (the header's buttons,
 * `LewFetchProject`'s compact form).
 *
 * `open` is the drawer's model. Minimised is not a way of closing: the panel stays open, and closing
 * it (✕, Esc, saving) always brings it back to its width for next time.
 *
 * `beforeClose` answers whether the panel may close now; the drawer asks there before unsaved
 * changes are thrown away.
 *
 * `describe` names the report on screen for a bug report (see {@link openReportPanels}); only asked
 * while the panel is open.
 */
export function useReportPanel(
  panel: ReportPanelKey,
  open: Ref<boolean | undefined>,
  {
    beforeClose,
    describe,
  }: { beforeClose?: () => Promise<boolean>; describe?: () => ReportPanelReport | null | undefined } = {}
): ReportPanel {
  const { width: viewport } = useDisplay()

  const stored = readSize(panel)
  /** The width this person dragged the panel to. Only ever a width that fits {@link MIN_WIDTH}. */
  const preferred = ref(Math.max(stored.width ?? DEFAULT_WIDTH, MIN_WIDTH))
  const full = ref(stored.full ?? false)
  const minimized = ref(false)
  /** Set while the handle is held: the width follows the pointer, not {@link preferred}. */
  const dragWidth = ref<number | null>(null)
  let dragStart: { x: number; width: number } | null = null

  /** The viewport, less the strips of the other panel docked beside this one. */
  const room = computed(() => Math.max(viewport.value - (docked.size - (docked.has(panel) ? 1 : 0)) * STRIP_WIDTH, 0))
  const resizable = computed(() => viewport.value >= RESIZE_FROM)

  const width = computed(() => {
    if (minimized.value) return STRIP_WIDTH
    if (dragWidth.value !== null) return dragWidth.value
    if (full.value || !resizable.value) return room.value
    return Math.min(preferred.value, room.value)
  })

  const isFull = computed(() => !minimized.value && width.value >= room.value)
  const isNarrow = computed(() => width.value < RESIZE_FROM)
  const canToggleFull = computed(() => resizable.value && preferred.value < room.value)

  const dragIntent = computed(() => {
    if (dragWidth.value === null) return null
    if (dragWidth.value < MIN_WIDTH) return "minimize"
    if (dragWidth.value >= room.value - FULL_SNAP) return "full"
    return null
  })

  function persist() {
    writeSize(panel, { width: preferred.value, full: full.value })
  }

  /** Where a drag or a key press leaves the panel: its own width, or all of it when that is close. */
  function settle(next: number) {
    if (next >= room.value - FULL_SNAP) {
      full.value = true
    } else {
      full.value = false
      preferred.value = Math.round(clamp(next, MIN_WIDTH, room.value))
    }
    persist()
  }

  function minimize() {
    minimized.value = true
  }

  /** Back to the width it had. Whichever other panel was in front steps aside to a strip. */
  function restore() {
    minimized.value = false
  }

  function toggleFull() {
    full.value = !full.value
    persist()
  }

  function resetWidth() {
    full.value = false
    preferred.value = DEFAULT_WIDTH
    persist()
  }

  async function close() {
    if (beforeClose && !(await beforeClose())) return
    open.value = false
  }

  async function mayReplace(confirm: () => Promise<boolean>) {
    if (!minimized.value) return true
    if (!(await confirm())) return false
    restore()
    return true
  }

  const handle = {
    onPointerdown(event: PointerEvent) {
      if (event.button !== 0) return
      event.preventDefault()
      ;(event.currentTarget as Element).setPointerCapture(event.pointerId)
      dragStart = { x: event.clientX, width: width.value }
      dragWidth.value = width.value
    },
    onPointermove(event: PointerEvent) {
      if (!dragStart) return
      // The panel hangs off the right edge, so pulling the handle left widens it.
      dragWidth.value = clamp(dragStart.width + dragStart.x - event.clientX, DRAG_FLOOR, room.value)
    },
    onPointerup() {
      if (!dragStart || dragWidth.value === null) return
      const released = dragWidth.value
      const moved = released !== dragStart.width
      dragStart = null
      dragWidth.value = null
      // A click on the handle is not a choice of width: a full panel would lose its fullness to it.
      if (!moved) return
      if (released < MIN_WIDTH) minimize()
      else settle(released)
    },
    onPointercancel() {
      dragStart = null
      dragWidth.value = null
    },
    onKeydown(event: KeyboardEvent) {
      const step = event.shiftKey ? KEY_STEP * 4 : KEY_STEP
      let next: number
      if (event.key === "ArrowLeft") next = width.value + step
      else if (event.key === "ArrowRight") next = width.value - step
      else if (event.key === "Home") next = MIN_WIDTH
      else if (event.key === "End") next = room.value
      else return
      event.preventDefault()
      // The keys stop at the narrowest width: minimising is the header's button, not a key held too long.
      settle(clamp(next, MIN_WIDTH, room.value))
    },
    onDblclick: resetWidth,
  }

  // Closing is the end of the report either way, so the next one opens at its width, not as a strip.
  watch(open, isOpen => {
    if (!isOpen) minimized.value = false
  })

  // Dock and front: at most one panel covers the page. The one coming to the front sends the other
  // to the dock, so the strip of a report left for later is always one click away.
  watch(
    [() => Boolean(open.value), minimized],
    ([isOpen, isMinimized]) => {
      if (isOpen && isMinimized) docked.add(panel)
      else docked.delete(panel)
      if (isOpen && !isMinimized) {
        for (const [key, other] of fronts) if (key !== panel && other.isFront()) other.minimize()
      }
    },
    { immediate: true }
  )
  fronts.set(panel, { isFront: () => Boolean(open.value) && !minimized.value, minimize })
  const describePanel: DescribePanel = () => {
    const report = open.value ? describe?.() : null
    return report ? { ...report, panel, minimized: minimized.value } : null
  }
  if (describe) described.set(panel, describePanel)
  onScopeDispose(() => {
    docked.delete(panel)
    fronts.delete(panel)
    // Only its own: a page's panel mounted before the last one's page was gone has taken the key.
    if (described.get(panel) === describePanel) described.delete(panel)
  })

  const api: ReportPanel = {
    width,
    minimized,
    isFull,
    isNarrow,
    resizable,
    canToggleFull,
    dragIntent,
    dragging: computed(() => dragWidth.value !== null),
    minWidth: MIN_WIDTH,
    maxWidth: room,
    minimize,
    restore,
    toggleFull,
    resetWidth,
    close,
    mayReplace,
    handle,
  }
  provide(REPORT_PANEL, api)
  return api
}
